/**
 * Helpers for `date` search qualifiers (e.g. `created:>2026-01-01`).
 *
 * A date qualifier's URL value is an optional comparison operator followed by an
 * ISO date. These helpers turn that back and forth between the raw value, the
 * calendar panel's (operator, Date) pair, and a readable chip label.
 */

const ISO = 'YYYY-MM-DD';

/**
 * Phrase used for a chip and for the operator selector, e.g. `>` → "après le".
 * Keyed by operator so adding one is a single entry.
 */
export function dateOperatorLabels() {
    return {
        '=': i18n.t('on the'),
        '>': i18n.t('after the'),
        '<': i18n.t('before the'),
        '>=': i18n.t('as of'),
        '<=': i18n.t('up to')
    };
}

/**
 * Split a raw qualifier value into its operator and ISO date.
 *
 * @param {string} raw
 * @returns {{operator: string, date: string}}
 */
export function parseDateValue(raw) {
    const match = /^(>=|<=|!=|>|<|=)?(.*)$/.exec(String(raw ?? ''));
    return { operator: match?.[1] || '=', date: (match?.[2] || '').trim() };
}

/**
 * Human-facing label for a date chip, e.g. "après le 4 août 2026".
 *
 * @param {string} raw
 * @returns {string}
 */
export function formatDateValue(raw) {
    const { operator, date } = parseDateValue(raw);
    const parsed = moment(date, ISO, true);

    if (!parsed.isValid()) return String(raw ?? '');

    const phrase = dateOperatorLabels()[operator] || '';
    return `${phrase} ${parsed.format('LL')}`.trim();
}

/**
 * The date alone, written out — the operator is spelled by whoever owns the
 * chip, so it is not repeated here.
 *
 * @param {string} raw
 * @returns {string}
 */
export function formatDateOnly(raw) {
    const parsed = moment(String(raw ?? ''), ISO, true);

    return parsed.isValid() ? parsed.format('LL') : String(raw ?? '');
}

/**
 * Serialize a calendar selection back into a URL value.
 *
 * @param {string} operator
 * @param {Date} date
 * @returns {string}
 */
export function serializeDate(operator, date) {
    const iso = moment(date).format(ISO);
    return operator && operator !== '=' ? `${operator}${iso}` : iso;
}

/**
 * Parse a raw qualifier value into a Date the calendar can preselect.
 *
 * @param {string} raw
 * @returns {?Date}
 */
export function toDate(raw) {
    const parsed = moment(parseDateValue(raw).date, ISO, true);
    return parsed.isValid() ? parsed.toDate() : null;
}

/**
 * Relative shortcuts offered above the calendar, so the common cases never
 * require picking a day. Each returns a ready-to-use URL value.
 *
 * @returns {Array<{label: string, value: string}>}
 */
export function dateShortcuts() {
    return [
        { label: i18n.t('Today'), value: moment().format(ISO) },
        { label: i18n.t('Last 7 days'), value: `>=${moment().subtract(7, 'days').format(ISO)}` },
        { label: i18n.t('Last 30 days'), value: `>=${moment().subtract(30, 'days').format(ISO)}` },
        { label: i18n.t('This month'), value: `>=${moment().startOf('month').format(ISO)}` },
        { label: i18n.t('This year'), value: `>=${moment().startOf('year').format(ISO)}` }
    ];
}
